import type { ProductOfferingDTO } from '@t/api.types';
import type { ParsedEquipment } from '@t/equipmentOffering.types';
import { getCmsImageUrl } from '@utils/cmsImageUrl';
import { parseDescriptionJson } from '@utils/productOfferingMapper';

const getCharacteristic = (dto: ProductOfferingDTO, name: string) =>
  dto.productOfferingCharacteristic
    ?.find((c) => c.name === name)
    ?.characteristicValueSpecification?.find((s) => s.isDefault)?.value as string | undefined;

/**
 * Parses the rentPackageDescription JSON characteristic to extract image code and summary.
 */
const parseRentPackageDescription = (
  dto: ProductOfferingDTO
): {
  imageCode?: string;
  summary?: string;
  supportsQuantity?: boolean;
  discountTag?: string;
  discountMessage?: string;
} => {
  const raw = getCharacteristic(dto, 'rentPackageDescription');
  const parsed = parseDescriptionJson(raw);
  if (!parsed) return {};
  return {
    imageCode: (parsed as Record<string, unknown>).image as string | undefined,
    summary: parsed.summary ?? ((parsed as Record<string, unknown>).Summary as string | undefined),
    supportsQuantity: parsed.quantity === 'true',
    discountTag: parsed.discountTag as string | undefined,
    discountMessage: parsed.discountMessage as string | undefined,
  };
};

export const selectParsedEquipment = (data: ProductOfferingDTO[]): ParsedEquipment[] => data.map(parseEquipment);

export const parseEquipment = (dto: ProductOfferingDTO): ParsedEquipment => {
  const price = dto.productOfferingPrice?.[0];
  const rentPkg = parseRentPackageDescription(dto);
  const desc = parseDescriptionJson(dto.description);

  return {
    id: dto.id,
    name: dto.name,
    description: desc?.summary ?? ((desc as Record<string, unknown>)?.Summary as string) ?? rentPkg.summary ?? '',
    modelGroupId: dto.category?.[0]?.id ?? '',
    modelGroupName: dto.category?.[0]?.name ?? '',
    price: price?.price?.amount ?? 0,
    priceType: price?.priceType ?? 'recurring',
    code: getCharacteristic(dto, 'code') ?? getCharacteristic(dto, 'modelGroupCode'),
    maxServiceLines: Number(getCharacteristic(dto, 'maxServiceLines')) || undefined,
    imageUrl: getCmsImageUrl('device', rentPkg.imageCode) ?? getCmsImageUrl('device', getCharacteristic(dto, 'code')),
    discountTag: rentPkg.discountTag,
    discountMessage: rentPkg.discountMessage,
    supportsQuantity: rentPkg.supportsQuantity ?? false,
  };
};
